import React, { useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../services/translate';
import FormInput from '../components/FormInput';
import FormActions from '../components/FormActions';
import { WindowInformation } from '../services/window-information';
import { abuseReportService } from '../services/central-api/abuse-report';
import Toast from 'react-native-toast-message';
import { ClientError } from '../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyPhoneInput from '../components/MyPhoneInput';
import MyAppText from '../components/MyAppText';


interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    authenticatedUser?: AuthenticatedUser;
}

export default function ReportAbuseModal({ onClose, authenticatedUser, isModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [description, setDescription] = useState('');

    function clearValues() {
        setName('');
        setPhone('');
        setDescription('');
    }

    function isEmpty(value: string) {
        return !value || (value?.trim() === '');
    }

    function isFormValid() {
        return !isEmpty(description);
    }

    async function formSubmit() {
        try {
            await abuseReportService.reportAbuse({
                name: name?.trim() || undefined,
                phone: phone?.trim() || undefined,
                description,
                anonymous: !authenticatedUser
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            onClose();
            clearValues();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[
                    styles.formContainer,
                    windowInfo.isMobile ? { width: '95%' } : { width: 635 },
                    authenticatedUser ? { height: 350 } : { height: 500 }
                ]}>
                    <MyAppText style={styles.modalTitle}>{translate('reportAbuse')}</MyAppText>
                    <View style={{ rowGap: 10 }}>
                        {!authenticatedUser
                            ? <FormInput
                                label={translate('name') + ' (' + translate('optional') + ')'}
                                placeholder={translate('name')}
                                value={name}
                                onChangeText={setName}
                                invalid={() => false}
                            />
                            : null
                        }
                        {!authenticatedUser
                            ? <MyPhoneInput
                                label={translate('phone') + ' (' + translate('optional') + ')'}
                                placeholder={translate('phone')}
                                value={phone}
                                onChangeText={setPhone}
                                invalid={() => false}
                            />
                            : null
                        }
                        <FormInput
                            label={translate('description')}
                            placeholder={translate('description')}
                            value={description}
                            multiline={true}
                            numberOfLines={5}
                            onChangeText={setDescription}
                            invalid={isEmpty}
                            required={true}
                        />
                    </View>
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => {
                            onClose();
                            clearValues();
                        }}
                        disabled={!isFormValid()} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        height: 500,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalTitle: {
        fontSize: 24,
        color: theme.colors.labelColor,
        fontWeight: 'bold'
    },
}));
